import { supabase } from '@/lib/supabase/client'

/** Where a finalized public submission's record lands. */
export type DestinationStatus = 'in_progress' | 'submitted_for_review' | 'approved'

export interface PublicLink {
  id: string
  project_id: string
  title: string
  token: string
  form_ids: string[]
  destination_status: DestinationStatus
  collect_contact: boolean
  allow_files: boolean
  expires_at: string | null
  max_submissions: number | null
  submission_count: number
  rate_limit_per_hour: number
  rate_limit_per_day: number
  is_revoked: boolean
  created_at: string
}

export interface PublishedForm {
  id: string
  title: string
  has_file_fields: boolean
}

export interface PublicLinkInput {
  title: string
  form_ids: string[]
  destination_status: DestinationStatus
  collect_contact: boolean
  allow_files: boolean
  expires_at: string | null
  max_submissions: number | null
  rate_limit_per_hour: number
  rate_limit_per_day: number
}

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

/** Generate an opaque, URL-safe token for a share link. */
export function generatePublicToken(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Build the public fill URL for a token on the current origin. */
export function publicLinkUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}/p/${token}`
}

interface FetchResult {
  links: PublicLink[]
  forms: PublishedForm[]
  reviewMode: 'required' | 'none'
  enabled: boolean
}

export async function fetchPublicLinksData(projectId: string): Promise<FetchResult> {
  const [linksRes, formsRes, projectRes] = await Promise.all([
    supabase
      .from('public_form_links')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false }),
    supabase
      .from('forms')
      .select('id, title, current_version_id')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .eq('is_published', true),
    supabase.from('projects').select('config').eq('id', projectId).single(),
  ])
  if (linksRes.error) throw linksRes.error
  if (formsRes.error) throw formsRes.error
  if (projectRes.error) throw projectRes.error

  const versionIds = (formsRes.data ?? [])
    .map((f) => f.current_version_id)
    .filter((v): v is string => !!v)

  // Detect which published forms contain file/image fields (gates allow_files).
  const fileFormIds = new Set<string>()
  if (versionIds.length) {
    const { data: versions } = await supabase
      .from('form_versions')
      .select('id, form_id, definition_snapshot')
      .in('id', versionIds)
    for (const v of versions ?? []) {
      const def = v.definition_snapshot as { fields?: { field_type?: string }[] }
      if ((def.fields ?? []).some((f) => f.field_type === 'file' || f.field_type === 'image')) {
        fileFormIds.add(v.form_id)
      }
    }
  }

  const config = (projectRes.data?.config as Record<string, unknown>) ?? {}

  return {
    links: (linksRes.data ?? []) as PublicLink[],
    forms: (formsRes.data ?? []).map((f) => ({
      id: f.id,
      title: parseTitle(f.title),
      has_file_fields: fileFormIds.has(f.id),
    })),
    reviewMode: (config.review_mode as 'required' | 'none') ?? 'required',
    enabled: Boolean(config.public_links_enabled),
  }
}

export async function createPublicLink(
  projectId: string,
  userId: string,
  input: PublicLinkInput,
): Promise<PublicLink> {
  const token = generatePublicToken()
  const { data, error } = await supabase
    .from('public_form_links')
    .insert({
      project_id: projectId,
      title: input.title,
      token,
      form_ids: input.form_ids,
      destination_status: input.destination_status,
      collect_contact: input.collect_contact,
      allow_files: input.allow_files,
      expires_at: input.expires_at,
      max_submissions: input.max_submissions,
      rate_limit_per_hour: input.rate_limit_per_hour,
      rate_limit_per_day: input.rate_limit_per_day,
      created_by: userId,
    })
    .select('*')
    .single()
  if (error) throw error
  return data as PublicLink
}

export async function revokePublicLink(linkId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('public_form_links')
    .update({ is_revoked: true, revoked_at: new Date().toISOString(), revoked_by: userId })
    .eq('id', linkId)
  if (error) throw error
}
